import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download, ListChecks, Search } from 'lucide-react';
import { adminAuditLogs } from '@/api/admin';
import type { AuditLogEntry } from '@/api/extras';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pill } from '@/components/widgets/Pill';
import { fmtDateTime } from '@/lib/format';
import { downloadCsv } from '@/lib/csv';
import { cn } from '@/lib/utils';

const ACTION_TONE: Record<string, 'brand' | 'warn' | 'down' | 'up' | 'neutral'> = {
  follow_mode_change: 'brand',
  follow_ratio_change: 'brand',
  symbol_preset: 'brand',
  symbol_param: 'brand',
  pending_apply_now: 'warn',
  force_liquidation: 'down',
  emergency_stop_set: 'down',
  emergency_stop_clear: 'up',
  session_update: 'brand',
  risk_config: 'brand',
  login: 'neutral',
};

const ACTION_LABEL: Record<string, string> = {
  follow_mode_change: '模式切换',
  follow_ratio_change: '比例修改',
  symbol_preset: '预设切换',
  symbol_param: '参数修改',
  pending_apply_now: '立即应用',
  force_liquidation: '强制平仓',
  emergency_stop_set: '紧急停机',
  emergency_stop_clear: '解除停机',
  session_update: '时段配置',
  risk_config: '风控配置',
  login: '登录',
};

const ACTION_FILTERS: Array<{ label: string; value: string }> = [
  { label: '全部', value: 'ALL' },
  { label: '模式/比例', value: 'follow_mode_change' },
  { label: '预设/参数', value: 'symbol_param' },
  { label: '立即应用', value: 'pending_apply_now' },
  { label: '强平/停机', value: 'force_liquidation' },
];

export function AuditLog() {
  const [action, setAction] = useState<string>('ALL');
  const [search, setSearch] = useState('');

  const listQ = useQuery({
    queryKey: ['admin', 'audit-logs', action],
    queryFn: () => adminAuditLogs.list({ pageSize: 100, ...(action !== 'ALL' ? { action } : {}) }),
  });

  const items = useMemo(() => {
    const all = listQ.data?.items ?? [];
    if (!search) return all;
    const q = search.trim().toLowerCase();
    return all.filter(
      (a) =>
        a.adminUsername.toLowerCase().includes(q) ||
        a.resource.toLowerCase().includes(q) ||
        (a.reason ?? '').toLowerCase().includes(q),
    );
  }, [listQ.data, search]);

  // 导出当前筛选结果(action 筛选 + 搜索后的 items),复用 §9.15 报表的前端 CSV 模式。
  const exportCsv = () => {
    downloadCsv('audit-logs', items, [
      { key: 'ts', header: '时间', format: (v) => fmtDateTime(String(v)) },
      { key: 'adminUsername', header: '管理员' },
      { key: 'ip', header: 'IP' },
      { key: 'action', header: '动作', format: (v) => ACTION_LABEL[String(v)] ?? String(v) },
      { key: 'resource', header: '资源' },
      { key: 'before', header: 'BEFORE', format: (v) => (v != null ? JSON.stringify(v) : '') },
      { key: 'after', header: 'AFTER', format: (v) => (v != null ? JSON.stringify(v) : '') },
      { key: 'reason', header: '原因/备注' },
    ]);
  };

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-brand" />
            审计日志
          </h1>
          <p className="text-sm text-ink-2 mt-1">所有写操作流水 · 不可编辑 · 多维筛选 · 前后值 diff(§9.13)</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-ink-3" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索管理员 / 资源 / 原因"
              className="pl-7 w-64"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={items.length === 0}
            title={`导出当前筛选结果 ${items.length} 条`}
            onClick={exportCsv}
          >
            <Download className="h-4 w-4" />
            导出 CSV
          </Button>
        </div>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        {ACTION_FILTERS.map((f) => (
          <Button key={f.value} variant={action === f.value ? 'default' : 'outline'} size="sm" onClick={() => setAction(f.value)}>
            {f.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>流水 · {listQ.data?.total ?? '—'} 条</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-3 py-2 text-left font-normal">时间</th>
                  <th className="px-3 py-2 text-left font-normal">管理员</th>
                  <th className="px-3 py-2 text-left font-normal">IP</th>
                  <th className="px-3 py-2 text-left font-normal">动作</th>
                  <th className="px-3 py-2 text-left font-normal">资源</th>
                  <th className="px-3 py-2 text-left font-normal">原因 / 备注</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <AuditRow key={entry.id} entry={entry} />
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-ink-3">
                      没有符合条件的日志
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [open, setOpen] = useState(false);
  const hasDiff = entry.before || entry.after;
  return (
    <>
      <tr className={cn('border-t border-line cursor-pointer row-hover', hasDiff ? '' : 'cursor-default')} onClick={() => hasDiff && setOpen((o) => !o)}>
        <td className="px-2 py-2 text-ink-3">
          {hasDiff ? open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" /> : null}
        </td>
        <td className="px-3 py-2 num text-xs text-ink-2">{fmtDateTime(entry.ts)}</td>
        <td className="px-3 py-2 num text-ink-1">{entry.adminUsername}</td>
        <td className="px-3 py-2 num text-xs text-ink-3">{entry.ip}</td>
        <td className="px-3 py-2">
          <Pill tone={ACTION_TONE[entry.action] ?? 'neutral'}>{ACTION_LABEL[entry.action] ?? entry.action}</Pill>
        </td>
        <td className="px-3 py-2 num text-xs text-ink-1">{entry.resource}</td>
        <td className="px-3 py-2 text-xs text-ink-2 truncate max-w-[300px]">{entry.reason ?? ''}</td>
      </tr>
      {open && hasDiff && (
        <tr className="border-t border-line bg-bg-3/40">
          <td colSpan={7} className="px-8 py-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <div className="text-ink-3 font-semibold mb-1">BEFORE</div>
                <pre className="num text-ink-2 bg-bg-2 rounded-md p-2 overflow-x-auto whitespace-pre-wrap">
                  {entry.before ? JSON.stringify(entry.before, null, 2) : '—'}
                </pre>
              </div>
              <div>
                <div className="text-warn font-semibold mb-1">AFTER</div>
                <pre className="num text-ink-1 bg-bg-2 rounded-md p-2 overflow-x-auto whitespace-pre-wrap">
                  {entry.after ? JSON.stringify(entry.after, null, 2) : '—'}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
